/** @jsxRuntime classic */
/** @jsx jsx */

import React, { useEffect, useMemo, useState } from 'react';
import { useTheme, jsx } from '@emotion/react';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';

import { Droppable } from '../Droppable';
import { useMiddlewareContext } from '../../hooks/useMiddlewareContext';
import { WeeklyDraggable } from './WeeklyDraggable';
import { MoreSlotsList } from '../MoreSlotsList';
import { MoreSlotsButton } from '../MoreSlotsButton';
import { LoadingContainer } from '../LoadingContainer';
import { IWeekToDropProps, ECalendarView, IOptions, ISlotDateGroup } from '../../types';
import {
	spaceHeight,
	weekDayContainer,
	weekDayEventContainer,
	weekdayHeight,
	weekDayToDropContainer,
	weekDayToDropHorizontalContainer,
} from '../styles';
import { groupSlotsByDate } from '../../utils';
import { useResourceSlots } from '../../hooks/useResourceSlots';
import { ISlot } from '../../../organisms/bo_calendar_slot/types';
import { IColors } from '../../../types';
import { DateTimeFormats } from '@bondsports/date-time';

dayjs.extend(isoWeek);

const WEEKLY_SLOT_ROW_HEIGHT_PX = 20;
const WEEKLY_SLOT_LIST_PADDING_PX = 4;
const DAYS_PER_WEEK = 7;

export const WeekToDrop = ({ resourceId, EventComponent, isLoading }: IWeekToDropProps) => {
	const colors: IColors = useTheme();
	const { slots } = useResourceSlots({ resourceId: Number(resourceId) });
	const { options } = useMiddlewareContext();

	const { view = ECalendarView.VERTICAL, date, isSundayFirstDay } = options || {};

	const dayShift: number = isSundayFirstDay ? 1 : 0;
	const horizontal: boolean = view === ECalendarView.HORIZONTAL;
	const today: dayjs.Dayjs = dayjs.utc(date);

	const dates: dayjs.Dayjs[] = useMemo(
		() => Array.from({ length: DAYS_PER_WEEK }, (_, i) => today.isoWeekday(i + 1 - dayShift)),
		[date, dayShift]
	);

	const weekDaysWithSlots: ISlotDateGroup[] = useMemo(() => {
		return groupSlotsByDate(dates, slots);
	}, [dates, slots]);

	const [modals, setModals] = useState<boolean[]>([]);

	const handleToggleModal = (index: number) => {
		const newModals: boolean[] = structuredClone(modals);
		newModals[index] = !newModals[index];
		setModals(newModals);
	};

	const todayContainerId: string = `week_container_${resourceId}`;
	const todayContainer: HTMLElement = document.getElementById(todayContainerId)!;

	const maxVisibleSlotsPerDay: number = horizontal
		? Math.max(1, Math.floor((spaceHeight - WEEKLY_SLOT_LIST_PADDING_PX) / WEEKLY_SLOT_ROW_HEIGHT_PX))
		: !Number.isNaN(todayContainer?.clientHeight! - WEEKLY_SLOT_ROW_HEIGHT_PX)
			? Math.floor((todayContainer.clientHeight - WEEKLY_SLOT_ROW_HEIGHT_PX) / WEEKLY_SLOT_ROW_HEIGHT_PX) + 1
			: 1;

	useEffect(() => {
		setModals(Array(dates.length).fill(false));
	}, [dates]);

	return (
		<div css={horizontal ? weekDayToDropHorizontalContainer(colors) : weekDayToDropContainer(colors)}>
			{weekDaysWithSlots.map((weekDay, index) => (
				<WeekToDropContainer
					key={`${resourceId}_${weekDay.date.toISOString()}`}
					weekDay={weekDay}
					index={index}
					options={options}
					resourceId={resourceId}
					todayContainerId={todayContainerId}
					isLoading={isLoading}
					EventComponent={EventComponent}
					handleToggleModal={handleToggleModal}
					modals={modals}
					horizontal={horizontal}
					colors={colors}
					maxVisibleSlotsPerDay={maxVisibleSlotsPerDay}
				/>
			))}
		</div>
	);
};

type WeekToDropContainerProps = {
	resourceId: string;
	EventComponent: React.FC<any>;
	isLoading: boolean | undefined;
	weekDay: any;
	index: number;
	handleToggleModal: (index: number) => void;
	maxVisibleSlotsPerDay: number;
	horizontal: boolean;
	todayContainerId: string;
	modals: boolean[];
	colors: IColors;
	options: IOptions;
};

const WeekToDropContainer: React.FC<WeekToDropContainerProps> = ({
	weekDay,
	resourceId,
	handleToggleModal,
	maxVisibleSlotsPerDay,
	horizontal,
	index,
	todayContainerId,
	isLoading,
	EventComponent,
	modals,
	colors,
	options,
}) => {
	const { slots: weekDaySlots } = weekDay;
	const droppableId: string = `${resourceId}_${weekDay.date.toISOString()}`;

	const withModal: boolean = weekDaySlots.length > maxVisibleSlotsPerDay;
	const previewSlotCount: number = withModal ? Math.max(1, maxVisibleSlotsPerDay - 1) : weekDaySlots.length;
	const moreItemsCount: number = weekDaySlots.length - previewSlotCount;

	const currentDate: string = weekDay.date.format(DateTimeFormats.YYYY_MM_DD);

	const slots: ISlot[] = useMemo(
		() => (withModal ? weekDaySlots.slice(0, previewSlotCount) : weekDaySlots),
		[weekDaySlots, withModal, previewSlotCount]
	);

	return (
		<Droppable
			weekly
			height={!horizontal ? `max(100% / 7, ${weekdayHeight}px)` : undefined}
			dragging={false}
			id={droppableId}
			key={droppableId}
		>
			<div id={index === 0 ? todayContainerId : undefined} css={weekDayContainer(colors, horizontal)}>
				{isLoading ? (
					<LoadingContainer />
				) : (
					slots.map(slot => {
						return (
							<WeeklyDraggable parentId={droppableId} state={slot} key={slot.id} id={slot.id}>
								<div css={weekDayEventContainer} key={slot.id}>
									<EventComponent
										event={slot}
										key={`EventComponent-${slot.id}`}
										currentDate={currentDate}
										mode={options.mode}
									/>
								</div>
							</WeeklyDraggable>
						);
					})
				)}
				{withModal && (
					<MoreSlotsButton
						isWeekly
						colors={colors}
						handleToggleModal={() => handleToggleModal(index)}
						moreItemsCount={moreItemsCount}
					/>
				)}
				{withModal && modals[index] && (
					<MoreSlotsList
						colors={colors}
						EventComponent={EventComponent}
						group={{ items: weekDaySlots }}
						handleToggleModal={handleToggleModal}
						index={index}
						currentDate={currentDate}
					/>
				)}
			</div>
		</Droppable>
	);
};
